import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, Image, Sparkles, BookOpen, Layout, Award, Smartphone, Target, MousePointer, Play, BarChart3, HelpCircle, Navigation } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AmazonAPlus = () => {
  const aplusServices = [
    'Professional A+ module design',
    'Product feature highlight sections',
    'Comparison charts and product benefits',
    'Lifestyle and product usage visuals',
    'Structured layouts optimized for desktop and mobile',
    'Conversion-focused content presentation'
  ];

  const premiumFeatures = [
    { icon: MousePointer, title: 'Interactive Hotspot Modules', description: 'Clickable areas on images that reveal additional information' },
    { icon: Play, title: 'Multiple Video Modules', description: 'Ability to showcase product videos and demonstrations' },
    { icon: BarChart3, title: 'Enhanced Comparison Charts', description: 'For comparing multiple products effectively' },
    { icon: Image, title: 'Larger Images', description: 'Up to 1464 x 600 pixels (compared to 970 x 300 in basic A+)' },
    { icon: Layout, title: 'Carousel Modules', description: 'For rotating content and showcasing multiple features' },
    { icon: HelpCircle, title: 'Q&A Modules', description: 'To answer common product questions directly' },
    { icon: Navigation, title: 'Navigation Carousels', description: 'For improved browsing experience' }
  ];

  const eligibilityRequirements = [
    'Must have A+ Brand Story published across their ASIN catalog',
    'Must have at least 5 approved A+ Content projects published within the past 12 months',
    'Must be a registered brand owner through Amazon Brand Registry'
  ];

  const brandStoryCards = [
    'Brand logo',
    'Text content',
    'Product images',
    'Links to Amazon Brand Store',
    'Links to other products in the brand catalog'
  ];

  const infographicServices = [
    'Product feature highlight images',
    'Product benefit explanations',
    'Product usage and lifestyle visuals',
    'Comparison graphics',
    'Specification and feature graphics',
    'Conversion-focused design layouts'
  ];

  const whyChooseUs = [
    { icon: Award, title: 'Professional Amazon Design Expertise', description: 'Our team specializes in Amazon-specific design requirements and best practices.' },
    { icon: Target, title: 'Conversion-Focused Visual Strategy', description: 'Every design element is crafted to maximize customer engagement and conversions.' },
    { icon: Smartphone, title: 'Mobile-Optimized A+ Layouts', description: 'Designs that look stunning and perform well on all devices.' },
    { icon: BookOpen, title: 'Strong Brand Storytelling Approach', description: 'We help brands connect with customers through compelling visual narratives.' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Amazon A+ Content & Infographic Design Services | Obiquiste</title>
        <meta name="description" content="Professional Amazon A+ Content design: enhanced brand content, infographics & lifestyle images. Increase conversions with visually stunning product pages." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/amazon-a-plus-content-design" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/amazon-account-management-services" className="text-slate-700 hover:text-primary font-medium">Amazon</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">A+ Content & Infographics</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(255, 193, 7, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Amazon A+ Content Design & <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">Infographic Services</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Elevate Your Product Presentation. Boost Conversions.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                High-quality visual content plays a major role in improving product presentation and conversion rates on Amazon. At Obiquiste Solutions, we help brands enhance their product detail pages using A+ Content, Premium A+ modules, Brand Story content, and infographic product images that highlight features and improve customer engagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=amazon-aplus">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Design Consultation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=amazon-aplus">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    View Portfolio
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon A+ Content (Enhanced Brand Content) */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-xl mb-6">
                <Layout className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Amazon A+ Content (Enhanced Brand Content)
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                A+ Content, also known as Enhanced Brand Content, allows brands to improve their product detail pages by adding rich images, feature modules, comparison charts, and visually structured content.
              </p>
              <p className="text-lg text-slate-700 mb-6">
                A+ Content helps brands showcase product features, benefits, and product story more effectively compared to normal product descriptions. Well-designed A+ Content improves customer understanding and helps increase conversion rates.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our A+ Content Services:</h3>
                <div className="space-y-4">
                  {aplusServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Premium A+ Content */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Premium A+ Content
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto">
              Premium A+ Content is an enhanced version of Amazon's A+ Content that allows brands to create more engaging and interactive product detail pages with advanced modules and larger visual elements.
            </p>
          </motion.div>

          {/* Key Features of Premium A+ */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              className="text-center mb-10"
            >
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
                Key Features of Premium A+
              </h3>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {premiumFeatures.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="font-heading text-lg font-bold mb-2">{feature.title}</h4>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Eligibility Requirements */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="font-heading text-2xl font-bold mb-6 text-center">
                Eligibility Requirements for Premium A+
              </h3>
              <p className="text-slate-700 mb-6 text-center">
                Brands must meet the following conditions to access Premium A+:
              </p>
              <div className="space-y-4 max-w-2xl mx-auto">
                {eligibilityRequirements.map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-amber-800 text-center">
                  <strong>Note:</strong> Amazon evaluates eligibility regularly and Premium A+ is currently available at no cost during the promotional period.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Amazon A+ Brand Story */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Brand Story Content Cards Can Include:</h3>
                <div className="space-y-4">
                  {brandStoryCards.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-blue-800 text-sm">
                    <strong>Tip:</strong> Brand Story and regular A+ Content can appear together on the same product page for maximum impact.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-xl mb-6">
                <BookOpen className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Amazon A+ Brand Story
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                A+ Brand Story creates a "From the Brand" section above the standard A+ Content on the product detail page. This feature allows brands to display their story using a horizontal carousel with a full-width background.
              </p>
              <p className="text-lg text-slate-700 mb-4">
                Brands can add up to <strong>19 content cards</strong> containing brand elements, product images, and links to their Amazon Brand Store or other products in the brand catalog.
              </p>
              <p className="text-lg text-slate-700">
                This section allows customers to scroll horizontally and explore more about the brand, creating a stronger connection with the product and increasing brand loyalty.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Infographic Product Images */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-xl mb-6">
                <Image className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Infographic Product Images
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Infographic images appear in the product image gallery and are often the first visuals customers see when they visit a listing. These images highlight key product features, benefits, and specifications in a visually engaging format.
              </p>
              <p className="text-lg text-slate-700">
                Optimized infographic images help customers understand the product quickly and improve purchase decisions, leading to higher conversion rates and reduced returns.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Infographic Services:</h3>
                <div className="space-y-4">
                  {infographicServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Why Choose Obiquiste Solutions
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Partner with Amazon design experts who understand what converts.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-300">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="mt-10 text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 px-6 py-3 rounded-full">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-slate-200">Designed to improve product engagement and conversions</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brands We Work With */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4 mr-2" />
              Our Clients
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Amazon Brands We Currently Work With
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We design A+ Content and infographics for 20+ brands across diverse categories, enhancing their product pages.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Brand</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Product / Focus</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Category</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { brand: 'Nutri Hut', product: 'FMCG / Nutrition Products', category: 'FMCG / Food & Nutrition' },
                  { brand: 'Rayon Caboki', product: 'Hair/Beauty Products', category: 'Beauty & Personal Care / Hair Care' },
                  { brand: 'Ayurdaanta Herbs', product: 'Ayurvedic Herbs & Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Ayur Devam', product: 'Ayurvedic/FMCG Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Suparna Trikha', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Haute', product: 'Gas Stoves', category: 'Home & Kitchen / Kitchen Appliances' },
                  { brand: 'GK Trading', product: 'Wallpapers', category: 'Home Improvement / Home Décor' },
                  { brand: 'Yuri Woori', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Mooncntrl', product: 'Apparel', category: 'Fashion / Apparel' },
                  { brand: 'Indian Kitchen Foods', product: 'Ready-to-Eat Meals', category: 'FMCG / Packaged Food & Ready-to-Eat' },
                  { brand: 'Earthvya', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'GW Khamkar', product: 'FMCG Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Vintage Oils', product: 'Oils / Edible Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Fitelo', product: 'Smart Weighing Scales', category: 'Electronics / Smart Health Devices' },
                  { brand: 'Fitty', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'RajGharana Metals', product: 'Kitchen Utensils', category: 'Home & Kitchen / Cookware & Kitchenware' },
                  { brand: 'HII Health Inside It', product: 'Health & Wellness Products', category: 'Health & Wellness' },
                  { brand: 'Auli Lifestyle', product: 'Skincare & Beauty Products', category: 'Beauty & Personal Care / Skincare' },
                  { brand: 'EHOP', product: 'TV & Electronic Remotes', category: 'Electronics / Consumer Electronics Accessories' },
                  { brand: 'Lavenust', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                ].map((item, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 hover:bg-orange-50/50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <span className="font-semibold text-slate-800">{item.brand}</span>
                    </td>
                    <td className="py-4 px-6 text-slate-600">{item.product}</td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {item.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-12 border border-orange-200">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Ready to Enhance Your Product Pages?
            </h2>
            <p className="text-lg md:text-xl text-slate-700 mb-8">
              Let our design experts create stunning A+ Content and infographics that showcase your products and drive conversions.
            </p>
            <Link to="/contact?service=amazon-aplus">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Free Design Consultation
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonAPlus;
